import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveImage } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const DIFFICULTY_COLORS = { easy: '#22c55e', medium: '#eab308', hard: '#ef4444' };
const DIFFICULTY_LABELS = { easy: 'Facil', medium: 'Medio', hard: 'Dificil' };

function ClockIcon({ color }) {
    return (
        <svg className="w-4 h-4 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
}

function ServingsIcon({ color }) {
    return (
        <svg className="w-4 h-4 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    );
}

function PrintIcon() {
    return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
    );
}

export default function RecipeSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'card';

    const ingredients = content.ingredients || [];
    const steps = content.steps || [];
    const nutrition = content.nutrition || [];
    const difficulty = content.difficulty || 'medium';
    const prepTime = parseInt(content.prep_time) || 0;
    const cookTime = parseInt(content.cook_time) || 0;
    const totalTime = prepTime + cookTime;

    const [checkedIngredients, setCheckedIngredients] = useState({});

    const toggleIngredient = (i) => {
        setCheckedIngredients(prev => ({ ...prev, [i]: !prev[i] }));
    };

    const mainImage = resolveImage(content, 'image', mediaFolders) || content.image_url;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const diffColor = DIFFICULTY_COLORS[difficulty] || DIFFICULTY_COLORS.medium;
    const diffLabel = content.difficulty_label || DIFFICULTY_LABELS[difficulty] || difficulty;

    // Meta bar component
    const MetaBar = () => (
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {prepTime > 0 && (
                <div className="flex items-center gap-1.5 text-sm" style={{ color: s.textSub }}>
                    <ClockIcon color={s.accent} />
                    <span><span className="font-semibold" style={{ color: s.text }}>{prepTime} min</span> prep</span>
                </div>
            )}
            {cookTime > 0 && (
                <div className="flex items-center gap-1.5 text-sm" style={{ color: s.textSub }}>
                    <ClockIcon color={s.accent} />
                    <span><span className="font-semibold" style={{ color: s.text }}>{cookTime} min</span> coccion</span>
                </div>
            )}
            {totalTime > 0 && (
                <div className="flex items-center gap-1.5 text-sm" style={{ color: s.textSub }}>
                    <ClockIcon color={s.accent} />
                    <span><span className="font-semibold" style={{ color: s.text }}>{totalTime} min</span> total</span>
                </div>
            )}
            {content.servings && (
                <div className="flex items-center gap-1.5 text-sm" style={{ color: s.textSub }}>
                    <ServingsIcon color={s.accent} />
                    <span><span className="font-semibold" style={{ color: s.text }}>{content.servings}</span> porciones</span>
                </div>
            )}
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: diffColor }}>
                {diffLabel}
            </span>
        </div>
    );

    // Ingredients list
    const IngredientsList = () => (
        <div>
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: s.text }}>
                <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {content.ingredients_title || 'Ingredientes'}
            </h3>
            <ul className="space-y-2.5">
                {ingredients.map((ing, i) => (
                    <li key={i}
                        className="flex items-center gap-3 cursor-pointer select-none group"
                        onClick={() => toggleIngredient(i)}
                    >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${checkedIngredients[i] ? '' : 'group-hover:border-opacity-60'}`}
                            style={{
                                borderColor: checkedIngredients[i] ? s.accent : (s.dark ? 'rgba(255,255,255,0.3)' : '#d1d5db'),
                                backgroundColor: checkedIngredients[i] ? s.accent : 'transparent',
                            }}
                        >
                            {checkedIngredients[i] && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                        <span className={`text-sm transition-all ${checkedIngredients[i] ? 'line-through opacity-50' : ''}`} style={{ color: s.textSub }}>
                            {ing.amount && <span className="font-semibold" style={{ color: s.text }}>{ing.amount} {ing.unit} </span>}
                            {ing.item}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );

    // Steps list
    const StepsList = () => (
        <div>
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: s.text }}>
                <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                {content.steps_title || 'Preparacion'}
            </h3>
            <ol className="space-y-6">
                {steps.map((step, i) => {
                    const stepImage = step.image_url;
                    return (
                        <li key={i} className={`flex gap-4 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ transitionDelay: visible ? `${300 + i * 100}ms` : '0ms' }}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 text-white"
                                style={{ backgroundColor: s.accent }}>
                                {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{step.text}</p>
                                {stepImage && (
                                    <img src={stepImage} alt={`Paso ${i + 1}`} className="mt-3 rounded-xl w-full max-w-md aspect-video object-cover" loading="lazy" />
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );

    // Nutrition grid
    const NutritionGrid = () => nutrition.length > 0 ? (
        <div>
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: s.text }}>
                <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {content.nutrition_title || 'Informacion Nutricional'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {nutrition.map((n, i) => (
                    <div key={i} className="text-center p-3 rounded-xl" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
                        <div className="text-xl font-bold" style={{ color: s.accent }}>{n.value}<span className="text-xs font-medium ml-0.5" style={{ color: s.textSub }}>{n.unit}</span></div>
                        <div className="text-xs mt-0.5" style={{ color: s.textSub }}>{n.label}</div>
                    </div>
                ))}
            </div>
        </div>
    ) : null;

    // Print button
    const PrintButton = () => content.show_print ? (
        <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80 border"
            style={{ borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: s.textSub }}
        >
            <PrintIcon />
            {content.print_label || 'Imprimir'}
        </button>
    ) : null;

    // ── Full variant ──
    if (variant === 'full') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.description} config={config} s={s} />

                    {/* Hero image */}
                    {mainImage && (
                        <div className={`relative rounded-2xl overflow-hidden mb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            <img src={mainImage} alt={content.title} className="w-full aspect-[21/9] object-cover" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        </div>
                    )}

                    {/* Meta bar */}
                    <div className={`mb-10 transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: visible ? '200ms' : '0ms' }}>
                        <MetaBar />
                    </div>

                    {/* Two column layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div className="lg:col-span-1">
                            <div className="lg:sticky lg:top-8">
                                <IngredientsList />
                                <div className="mt-8">
                                    <NutritionGrid />
                                </div>
                                <div className="mt-6">
                                    <PrintButton />
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-2">
                            <StepsList />
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Minimal variant ──
    if (variant === 'minimal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="text-center mb-8">
                        {content.tag && (
                            <span className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                {content.tag}
                            </span>
                        )}
                        {content.title && <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: s.text }}>{content.title}</h2>}
                        {content.description && <p className="text-base" style={{ color: s.textSub }}>{content.description}</p>}
                    </div>

                    <div className={`mb-8 transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
                        <MetaBar />
                    </div>

                    <div className="space-y-10">
                        <IngredientsList />
                        <div className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }} />
                        <StepsList />
                        <div className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }} />
                        <NutritionGrid />
                    </div>

                    <div className="mt-8 text-center">
                        <PrintButton />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Card variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                <div className={`${card} max-w-3xl mx-auto overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    {/* Image header */}
                    {mainImage && (
                        <div className="relative">
                            <img src={mainImage} alt={content.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4">
                                <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: diffColor }}>
                                    {diffLabel}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="p-6 md:p-8">
                        {/* Description */}
                        {content.description && <p className="text-base mb-6 leading-relaxed" style={{ color: s.textSub }}>{content.description}</p>}

                        {/* Meta bar */}
                        <div className="mb-8 py-4 border-y" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                            <MetaBar />
                        </div>

                        {/* Content sections */}
                        <div className="space-y-8">
                            <IngredientsList />

                            <div className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }} />

                            <StepsList />

                            {nutrition.length > 0 && (
                                <>
                                    <div className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }} />
                                    <NutritionGrid />
                                </>
                            )}
                        </div>

                        {/* Print */}
                        <div className="mt-8 flex justify-end">
                            <PrintButton />
                        </div>
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
